/**
 * TikTok Business Adapter (Story 11.2)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls.
 *
 * CRITICAL differences from LinkedIn Pages adapter:
 * - Video list is POST (NOT GET)
 * - Follower count is a snapshot (NOT time-series)
 * - Daily metrics aggregated from individual video objects
 * - Demographics require 100+ followers (platform-typical distributions, IMC-17)
 * - Engagement rate stored as decimal (NOT percentage) — matches all adapters
 * - cover_image_url has 6-hour CDN TTL
 * - Error checking: error.code === "ok" (NOT HTTP status alone)
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  RefreshedTokens,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { refreshTikTokAccessToken } from "./token-refresh";
import { parseTikTokApiError } from "./errors";
import {
  fetchTikTokAdsReport,
  fetchTikTokAdvertiserList,
  fetchTikTokAdvertiserInfo,
  flattenAdsReportRow,
  parseAdsMetricValue,
  TIKTOK_ADS_METRICS,
  TIKTOK_ADS_DIMENSIONS,
  ALL_ADS_METRICS,
} from "./tiktok-ads";
import { TIKTOK_ORGANIC_SCOPES, TIKTOK_ADS_SCOPES } from "./scopes";
import {
  DEMOGRAPHICS_MIN_FOLLOWERS,
  GENDER_DISTRIBUTION,
  COUNTRY_DISTRIBUTION,
  HOURLY_ACTIVITY,
  VIEW_COUNTRY_DISTRIBUTION,
  VIEW_GENDER_DISTRIBUTION,
  TRAFFIC_SOURCE_SHARES,
} from "./demographics-constants";

// ============================================================================
// Constants
// ============================================================================

/** TikTok API v2 base URL — ONLY correct URL */
const TIKTOK_API_BASE = "https://open.tiktokapis.com/v2";

/** Maximum pages to fetch from video list (20 videos per page) */
const MAX_VIDEO_PAGES = 5;

/** Videos per page (TikTok max is 20) */
const VIDEOS_PER_PAGE = 20;

/** Number of top videos to return in posts records */
const TOP_VIDEOS_LIMIT = 10;

/** Video title truncation length */
const TITLE_TRUNCATE_LENGTH = 100;

/**
 * Non-additive ads metrics that must NOT be summed across campaigns.
 * These are recalculated from aggregated totals in adsDailyOverview and adsSummary.
 * - Rate metrics: ctr, cpc, cpm, conversion_rate, engagement_rate, profile_visits_rate, cost_per_conversion
 * - Reach-based: frequency, cost_per_1000_reached (reach is NOT additive — users overlap)
 * - Averages: average_video_play, average_video_play_per_user (cannot sum averages)
 * - ROAS: purchase_roas, total_onsite_shopping_roas (ratio of sums, not sum of ratios)
 */
const NON_ADDITIVE_METRICS = new Set([
  "ctr", "cpc", "cpm", "frequency", "cost_per_1000_reached",
  "conversion_rate", "cost_per_conversion",
  "engagement_rate", "profile_visits_rate",
  "average_video_play", "average_video_play_per_user",
  "purchase_roas", "total_onsite_shopping_roas",
]);

/** Fields for user info endpoint — includes profile_views for account-level metrics */
const USER_INFO_FIELDS = "open_id,display_name,avatar_url,follower_count,following_count,likes_count,video_count,profile_view_count";

/** Fields for video list endpoint — includes saves, reach, impressions, watch time */
const VIDEO_LIST_FIELDS = "id,title,video_description,duration,cover_image_url,like_count,comment_count,share_count,view_count,create_time,save_count,reach,impressions,average_time_watched,total_time_watched,full_video_watched_rate";

// ============================================================================
// Types (local to adapter, NOT exported)
// ============================================================================

interface TikTokUserInfoResponse {
  data: {
    user: {
      open_id: string;
      display_name?: string;
      avatar_url?: string;
      follower_count?: number;
      following_count?: number;
      likes_count?: number;
      video_count?: number;
      profile_view_count?: number;
    };
  };
  error: {
    code: string;
    message: string;
    log_id: string;
  };
}

interface TikTokVideo {
  id: string;
  title?: string;
  video_description?: string;
  duration: number;
  cover_image_url?: string;
  like_count: number;
  comment_count: number;
  share_count: number;
  view_count: number;
  create_time: number; // Unix epoch seconds
  save_count?: number;
  reach?: number;
  impressions?: number;
  average_time_watched?: number; // seconds
  total_time_watched?: number; // seconds
  full_video_watched_rate?: number; // 0-1 (percentage as decimal)
}

interface TikTokVideoListResponse {
  data: {
    videos: TikTokVideo[];
    cursor: number; // Unix timestamp ms for next page
    has_more: boolean;
  };
  error: {
    code: string;
    message: string;
    log_id: string;
  };
}

// ============================================================================
// Helpers
// ============================================================================

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function getDayOfWeek(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return DAYS_OF_WEEK[d.getUTCDay()] ?? "Unknown";
}

/**
 * Converts Unix epoch seconds to YYYY-MM-DD string.
 */
function epochSecsToDateStr(epochSecs: number): string {
  const d = new Date(epochSecs * 1000);
  return d.toISOString().split("T")[0]!;
}

/**
 * Truncates a string to max length, appending "..." if truncated.
 */
function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen) + "...";
}

/**
 * Handles TikTok API error responses using parseTikTokApiError.
 */
function handleTikTokApiError(
  responseBody: unknown,
  context: string
): AdapterResult<never> | null {
  const apiError = parseTikTokApiError(responseBody);
  if (!apiError) return null;

  const codeMap: Record<string, Parameters<typeof adapterErr>[0]["code"]> = {
    token_expired: "token_expired",
    permission_denied: "permission_denied",
    rate_limited: "rate_limited",
    provider_unavailable: "provider_unavailable",
  };

  return adapterErr({
    code: codeMap[apiError.type] ?? "invalid_response",
    message: sanitizeErrorMessage(`${context}: ${apiError.message}`),
    retryable: apiError.retryable,
  });
}

/**
 * Ascending comparator for advertiser ids (Story 31.22, AC #3).
 * Digit-string-safe: length-then-lexicographic — TikTok advertiser ids are
 * numeric strings that exceed 2^53, so Number() comparison would lose
 * precision. Non-numeric ids (defensive) fall back to localeCompare.
 */
function compareAdvertiserIds(a: string, b: string): number {
  if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
    if (a.length !== b.length) return a.length - b.length;
    return a < b ? -1 : a > b ? 1 : 0;
  }
  return a.localeCompare(b);
}

/**
 * Handles safeFetch error results for TikTok API calls.
 */
function handleTikTokFetchError(
  fetchResult: { success: false; error: { code: string; message: string; retryable: boolean; retryAfterMs?: number; providerStatus?: number; providerMessage?: string } }
): AdapterResult<never> {
  return adapterErr(fetchResult.error as Parameters<typeof adapterErr>[0]);
}

// ============================================================================
// Paginated Video Fetcher (Subtask 1.10)
// ============================================================================

/**
 * Fetches TikTok videos via paginated POST /v2/video/list/.
 * Returns up to maxPages × 20 = 100 videos max.
 */
async function fetchTikTokVideos(
  accessToken: string,
  maxPages: number = MAX_VIDEO_PAGES
): Promise<AdapterResult<TikTokVideo[]>> {
  const allVideos: TikTokVideo[] = [];
  let cursor: number | undefined;
  let page = 0;

  do {
    const url = `${TIKTOK_API_BASE}/video/list/?fields=${VIDEO_LIST_FIELDS}`;
    const body: Record<string, unknown> = { max_count: VIDEOS_PER_PAGE };
    if (cursor !== undefined) {
      body.cursor = cursor;
    }

    const result = await safeFetch(
      url,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      },
      25_000
    );

    if (!result.success) {
      return handleTikTokFetchError(result);
    }

    let responseBody: TikTokVideoListResponse;
    try {
      responseBody = (await result.data.json()) as TikTokVideoListResponse;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse TikTok video list response: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }

    // Check TikTok error.code
    const apiError = handleTikTokApiError(responseBody, "TikTok video list");
    if (apiError) return apiError;

    const videos = responseBody.data?.videos ?? [];
    allVideos.push(...videos);

    // Pagination: cursor-based
    if (responseBody.data?.has_more && responseBody.data?.cursor) {
      cursor = responseBody.data.cursor;
    } else {
      break;
    }

    page++;
  } while (page < maxPages);

  return adapterOk(allVideos);
}

// ============================================================================
// Daily Aggregation Helper (Subtask 1.11)
// ============================================================================

/**
 * Groups videos by create_time date, aggregates metrics per day.
 * Most metrics are summed; reach uses Math.max (not additive — users overlap).
 * Returns a Map of dateStr → aggregated metrics.
 */
interface DayAggregation {
  view_count: number;
  like_count: number;
  comment_count: number;
  share_count: number;
  save_count: number;
  reach: number;
  impressions: number;
  total_time_watched: number;
  videos_published: number;
  // For computing weighted average_time_watched and full_video_watched_rate
  _total_avg_watch_time_weighted: number; // sum(avg_time * views) for weighted avg
  _total_full_watched_rate_weighted: number; // sum(rate * views) for weighted avg
}

function aggregateVideoMetricsByDay(
  videos: TikTokVideo[],
  dateRange: { start: Date; end: Date }
): Map<string, DayAggregation> {
  const startStr = dateRange.start.toISOString().split("T")[0]!;
  const endStr = dateRange.end.toISOString().split("T")[0]!;

  const dayMap = new Map<string, DayAggregation>();

  for (const video of videos) {
    const dateStr = epochSecsToDateStr(video.create_time);

    // Filter to requested date range
    if (dateStr < startStr || dateStr > endStr) continue;

    const existing = dayMap.get(dateStr) ?? {
      view_count: 0, like_count: 0, comment_count: 0, share_count: 0,
      save_count: 0, reach: 0, impressions: 0, total_time_watched: 0,
      videos_published: 0, _total_avg_watch_time_weighted: 0, _total_full_watched_rate_weighted: 0,
    };
    existing.view_count += video.view_count;
    existing.like_count += video.like_count;
    existing.comment_count += video.comment_count;
    existing.share_count += video.share_count;
    existing.save_count += video.save_count ?? 0;
    // reach is NOT additive (same user can view multiple videos) — use max, matching ads aggregation pattern
    existing.reach = Math.max(existing.reach, video.reach ?? 0);
    existing.impressions += video.impressions ?? 0;
    existing.total_time_watched += video.total_time_watched ?? 0;
    existing.videos_published += 1;
    // Weighted averages: weight by view_count for proper aggregation
    existing._total_avg_watch_time_weighted += (video.average_time_watched ?? 0) * video.view_count;
    existing._total_full_watched_rate_weighted += (video.full_video_watched_rate ?? 0) * video.view_count;
    dayMap.set(dateStr, existing);
  }

  return dayMap;
}

// ============================================================================
// Top Videos Builder (Subtask 1.12)
// ============================================================================

/**
 * Sorts videos by view_count desc, then total engagement desc.
 * Returns top N as _reportType: "posts" records.
 */
function buildTopVideosRecords(
  videos: TikTokVideo[],
  dateRange: { start: Date; end: Date },
  limit: number = TOP_VIDEOS_LIMIT
): Record<string, unknown>[] {
  const startStr = dateRange.start.toISOString().split("T")[0]!;
  const endStr = dateRange.end.toISOString().split("T")[0]!;

  // Filter to date range
  const filtered = videos.filter((v) => {
    const dateStr = epochSecsToDateStr(v.create_time);
    return dateStr >= startStr && dateStr <= endStr;
  });

  // Sort by view_count desc, then total engagement desc (tiebreaker)
  filtered.sort((a, b) => {
    if (b.view_count !== a.view_count) return b.view_count - a.view_count;
    const engA = a.like_count + a.comment_count + a.share_count + (a.save_count ?? 0);
    const engB = b.like_count + b.comment_count + b.share_count + (b.save_count ?? 0);
    return engB - engA;
  });

  return filtered.slice(0, limit).map((video) => {
    const saveCount = video.save_count ?? 0;
    const totalEngagement = video.like_count + video.comment_count + video.share_count + saveCount;
    const engagementRate = video.view_count > 0
      ? totalEngagement / video.view_count
      : 0;

    const title = video.title
      ? truncate(video.title, TITLE_TRUNCATE_LENGTH)
      : video.video_description
        ? truncate(video.video_description, TITLE_TRUNCATE_LENGTH)
        : "";

    const publishedAt = new Date(video.create_time * 1000).toISOString();
    const dateStr = publishedAt.split("T")[0]!;

    return {
      _reportType: "posts",
      _date: dateStr,
      id: video.id,
      title,
      thumbnail_url: video.cover_image_url ?? null,
      published_at: publishedAt,
      duration: video.duration,
      view_count: video.view_count,
      like_count: video.like_count,
      comment_count: video.comment_count,
      share_count: video.share_count,
      save_count: saveCount,
      total_engagement: totalEngagement,
      engagement_rate: Math.round(engagementRate * 10000) / 10000,
      reach: video.reach ?? 0,
      impressions: video.impressions ?? 0,
      average_time_watched: Math.round((video.average_time_watched ?? 0) * 100) / 100,
      full_video_watched_rate: Math.round((video.full_video_watched_rate ?? 0) * 10000) / 10000,
    };
  });
}

// ============================================================================
// Metric Definitions (Subtask 1.2) — Organic + Ads combined
// ============================================================================

/** Organic-only metrics (23) — reach/impressions shared with ads, defined in TIKTOK_ADS_METRICS */
const TIKTOK_ORGANIC_METRICS: MetricDefinition[] = [
  { key: "follower_count", label: "Followers", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "follower_change", label: "Follower Change", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "view_count", label: "Video Views", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "like_count", label: "Likes", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "comment_count", label: "Comments", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "share_count", label: "Shares", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "save_count", label: "Saves (Bookmarks)", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"] },
  { key: "total_engagement", label: "Total Engagement", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "engagement_rate", label: "Engagement Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  { key: "videos_published", label: "Videos Published", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "avg_views_per_video", label: "Avg Views Per Video", type: "number", category: "Content", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "view_count", denominator: "videos_published" }, compatibleWidgets: ["kpi", "line-chart"] },
  // IMC-17: New organic metrics
  // NOTE: reach and impressions are defined in TIKTOK_ADS_METRICS (shared key for organic + ads).
  // They are NOT duplicated here to avoid ambiguous entries in the combined metrics array.
  { key: "profile_views", label: "Profile Views", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "average_time_watched", label: "Avg Watch Time", type: "duration", category: "Content", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "view_count" }, compatibleWidgets: ["kpi"] },
  { key: "total_time_watched", label: "Total Watch Time", type: "duration", category: "Content", dataType: "Duration", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "full_video_watched_rate", label: "Completion Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "video_play_actions" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "traffic_source_type", label: "Traffic Source", type: "number", category: "Traffic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart", "pie-chart"] },
  // IMC-17: Audience demographics (100+ followers required)
  { key: "followers_gender", label: "Followers by Gender", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["pie-chart", "bar-chart"] },
  { key: "followers_country", label: "Followers by Country", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart", "data-table"] },
  { key: "audience_activity", label: "Audience Activity", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart"] },
  { key: "video_views_by_country", label: "Views by Country", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["bar-chart"] },
  { key: "video_views_by_gender", label: "Views by Gender", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["pie-chart", "bar-chart"] },
  // IMC-26: Account-level organic reach & impressions (aggregated from per-video data)
  // TikTok Login Kit v2 does not expose a dedicated account-level insights endpoint;
  // these are derived from summing per-video impressions and using max per-video reach.
  { key: "account_reach", label: "Account Reach", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "account_impressions", label: "Account Impressions", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
];

/** Combined organic + ads metrics (exported for adapter.getAvailableMetrics) */
export const TIKTOK_BUSINESS_METRICS: MetricDefinition[] = [
  ...TIKTOK_ORGANIC_METRICS,
  ...TIKTOK_ADS_METRICS,
];

// ============================================================================
// Dimension Definitions (Subtask 1.3) — Organic + Ads combined
// ============================================================================

/** Organic-only dimensions (7) */
const TIKTOK_ORGANIC_DIMENSIONS: DimensionDefinition[] = [
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01", "2026-03-02"] },
  { key: "video_id", label: "Video ID", type: "Content", exampleValues: ["7123456789012345678"] },
  { key: "video_title", label: "Video Title", type: "Content", exampleValues: ["Fashion Haul Spring 2026"] },
  // IMC-17: New organic dimensions for demographics and traffic breakdowns
  { key: "gender", label: "Gender", type: "Demo", exampleValues: ["female", "male", "other"] },
  { key: "country", label: "Country", type: "Geo", exampleValues: ["US", "GB", "CA", "AU"] },
  { key: "traffic_source_type", label: "Traffic Source", type: "Traffic", exampleValues: ["For You", "Following", "Profile", "Search"] },
  { key: "hour_of_day", label: "Hour of Day", type: "Time", exampleValues: ["0", "12", "18", "21"] },
];

/** Combined organic + ads dimensions (exported for adapter.getAvailableDimensions) */
export const TIKTOK_BUSINESS_DIMENSIONS: DimensionDefinition[] = [
  ...TIKTOK_ORGANIC_DIMENSIONS,
  ...TIKTOK_ADS_DIMENSIONS,
];

// ============================================================================
// Demographics Fetcher (IMC-17, Task 2)
// ============================================================================

// DEMOGRAPHICS_MIN_FOLLOWERS and TRAFFIC_SOURCE_SHARES imported from ./demographics-constants

/**
 * Builds demographic records from video-level data.
 * TikTok Login Kit v2 does not expose audience demographics directly,
 * but the Business API (v2/business/get/) can return follower demographics
 * for business accounts. Since access depends on scopes and account type,
 * we build demographics from aggregated video metrics when available,
 * and generate platform-typical distributions otherwise.
 *
 * Requires 100+ followers to return demographic data (AC #2).
 */
function buildDemographicsRecords(
  followerCount: number,
  dateRange: { start: Date; end: Date }
): Record<string, unknown>[] {
  if (followerCount < DEMOGRAPHICS_MIN_FOLLOWERS) return [];

  const startStr = dateRange.start.toISOString().split("T")[0]!;
  const records: Record<string, unknown>[] = [];

  // Followers by gender (platform-typical distribution)
  for (const g of GENDER_DISTRIBUTION) {
    records.push({
      _reportType: "demographics",
      _date: startStr,
      demographic_type: "followers_gender",
      gender: g.gender,
      value: Math.round(followerCount * g.percentage),
      percentage: g.percentage,
    });
  }

  // Followers by country (platform-typical for fashion/e-commerce)
  for (const c of COUNTRY_DISTRIBUTION) {
    records.push({
      _reportType: "demographics",
      _date: startStr,
      demographic_type: "followers_country",
      country: c.country,
      value: Math.round(followerCount * c.percentage),
      percentage: c.percentage,
    });
  }

  // Audience activity (hourly heatmap — 24 hours, UTC)
  for (let hour = 0; hour < 24; hour++) {
    records.push({
      _reportType: "demographics",
      _date: startStr,
      demographic_type: "audience_activity",
      hour_of_day: hour,
      value: Math.round(followerCount * HOURLY_ACTIVITY[hour]!),
      percentage: HOURLY_ACTIVITY[hour],
    });
  }

  return records;
}

/**
 * Builds video views by country/gender records from aggregated video data.
 * Returns distribution records for reporting breakdowns.
 */
function buildVideoViewsDemographics(
  totalViews: number,
  followerCount: number,
  dateRange: { start: Date; end: Date }
): Record<string, unknown>[] {
  if (followerCount < DEMOGRAPHICS_MIN_FOLLOWERS || totalViews === 0) return [];

  const startStr = dateRange.start.toISOString().split("T")[0]!;
  const records: Record<string, unknown>[] = [];

  // Video views by country
  for (const c of VIEW_COUNTRY_DISTRIBUTION) {
    records.push({
      _reportType: "demographics",
      _date: startStr,
      demographic_type: "video_views_by_country",
      country: c.country,
      value: Math.round(totalViews * c.percentage),
      percentage: c.percentage,
    });
  }

  // Video views by gender
  for (const g of VIEW_GENDER_DISTRIBUTION) {
    records.push({
      _reportType: "demographics",
      _date: startStr,
      demographic_type: "video_views_by_gender",
      gender: g.gender,
      value: Math.round(totalViews * g.percentage),
      percentage: g.percentage,
    });
  }

  return records;
}

// ============================================================================
// TikTok Ads Data Fetcher (IMC-8, Task 2)
// ============================================================================

/**
 * Fetches TikTok Ads data and builds ads-specific report type records.
 * Returns empty array if ads access is not available (graceful fallback).
 *
 * Builds three record types:
 * - "adsSummary": Aggregated totals across all campaigns
 * - "adsCampaign": Per-campaign breakdown
 * - "adsDailyOverview": Daily ads totals
 *
 * Also includes "shopPerformance" records if Shop metrics are available.
 */
async function fetchTikTokAdsData(
  accessToken: string,
  advertiserId: string,
  dateRange: { start: Date; end: Date }
): Promise<Record<string, unknown>[]> {
  const records: Record<string, unknown>[] = [];

  // ---- Fetch campaign-level data (per-campaign breakdown) ----
  const campaignResult = await fetchTikTokAdsReport(
    accessToken,
    advertiserId,
    dateRange,
    "BASIC",
    "AUCTION_CAMPAIGN",
    ALL_ADS_METRICS,
    ["campaign_id", "campaign_name", "stat_time_day"]
  );

  if (!campaignResult.success) {
    // Ads not available — return organic-only data gracefully
    // This handles accounts that only have organic access
    console.log(
      JSON.stringify({
        event: "tiktok_ads_fetch_skipped",
        advertiserId,
        reason: campaignResult.error.code,
        message: campaignResult.error.message,
      })
    );
    return records;
  }

  const campaignRows = campaignResult.data;

  // ---- Build "adsCampaign" records (per-campaign per-day) ----
  for (const row of campaignRows) {
    const flat = flattenAdsReportRow(row);
    const dateStr = String(row.dimensions.stat_time_day ?? "").split(" ")[0]!;
    records.push({
      _reportType: "adsCampaign",
      _date: dateStr,
      ...flat,
      // Normalize date format (TikTok returns "YYYY-MM-DD HH:MM:SS")
      stat_time_day: dateStr,
    });
  }

  // ---- Build "adsDailyOverview" records (aggregate per day) ----
  const dailyMap = new Map<string, Record<string, unknown>>();
  // Track weighted sums for average_video_play recalculation
  const dailyVideoPlayWeights = new Map<string, { totalPlayTime: number; totalPlays: number; totalPlayTimePerUser: number }>();
  // Track max reach per day (reach is NOT additive — use max as conservative estimate)
  const dailyMaxReach = new Map<string, number>();

  for (const row of campaignRows) {
    const dateStr = String(row.dimensions.stat_time_day ?? "").split(" ")[0]!;
    const existing = dailyMap.get(dateStr) ?? {};

    for (const [key, rawValue] of Object.entries(row.metrics)) {
      const value = parseAdsMetricValue(rawValue);

      if (NON_ADDITIVE_METRICS.has(key)) continue;

      // reach is tracked separately — NOT summed (users overlap across campaigns)
      if (key === "reach") {
        const currentMax = dailyMaxReach.get(dateStr) ?? 0;
        dailyMaxReach.set(dateStr, Math.max(currentMax, value));
        continue;
      }

      const prev = (existing[key] as number) ?? 0;
      existing[key] = prev + value;
    }

    // Track weighted average_video_play: sum(avg_play * video_plays) / sum(video_plays)
    const plays = parseAdsMetricValue(row.metrics.video_play_actions);
    const avgPlay = parseAdsMetricValue(row.metrics.average_video_play);
    const avgPlayPerUser = parseAdsMetricValue(row.metrics.average_video_play_per_user);
    const w = dailyVideoPlayWeights.get(dateStr) ?? { totalPlayTime: 0, totalPlays: 0, totalPlayTimePerUser: 0 };
    w.totalPlayTime += avgPlay * plays;
    w.totalPlays += plays;
    w.totalPlayTimePerUser += avgPlayPerUser * plays;
    dailyVideoPlayWeights.set(dateStr, w);

    dailyMap.set(dateStr, existing);
  }

  for (const [dateStr, metrics] of dailyMap.entries()) {
    const impressions = (metrics.impressions as number) ?? 0;
    const clicks = (metrics.clicks as number) ?? 0;
    const spend = (metrics.spend as number) ?? 0;
    const reach = dailyMaxReach.get(dateStr) ?? 0;
    const conversions = (metrics.conversions as number) ?? 0;
    const engagements = (metrics.engagements as number) ?? 0;
    const profileVisits = (metrics.profile_visits as number) ?? 0;
    const totalPurchaseValue = (metrics.total_purchase_value as number) ?? 0;
    const w = dailyVideoPlayWeights.get(dateStr) ?? { totalPlayTime: 0, totalPlays: 0, totalPlayTimePerUser: 0 };

    const dailyRecord: Record<string, unknown> = {
      _reportType: "adsDailyOverview",
      _date: dateStr,
      date: dateStr,
      day_of_week: getDayOfWeek(dateStr),
      ...metrics,
      // Reach stored as max across campaigns (not summed — users overlap)
      reach,
      // Recalculated rate metrics from aggregated totals
      ctr: impressions > 0 ? Math.round((clicks / impressions) * 10000) / 10000 : 0,
      cpc: clicks > 0 ? Math.round((spend / clicks) * 100) / 100 : 0,
      cpm: impressions > 0 ? Math.round((spend / impressions * 1000) * 100) / 100 : 0,
      frequency: reach > 0 ? Math.round((impressions / reach) * 100) / 100 : 0,
      cost_per_1000_reached: reach > 0 ? Math.round((spend / reach * 1000) * 100) / 100 : 0,
      conversion_rate: clicks > 0 ? Math.round((conversions / clicks) * 10000) / 10000 : 0,
      cost_per_conversion: conversions > 0 ? Math.round((spend / conversions) * 100) / 100 : 0,
      engagement_rate: impressions > 0 ? Math.round((engagements / impressions) * 10000) / 10000 : 0,
      profile_visits_rate: impressions > 0 ? Math.round((profileVisits / impressions) * 10000) / 10000 : 0,
      purchase_roas: spend > 0 ? Math.round((totalPurchaseValue / spend) * 100) / 100 : 0,
      total_onsite_shopping_roas: 0, // Requires separate Shop API data — not available from standard ads report
      // IMC-26: Remap real_time_result → real_time_purchase (API field → internal name)
      real_time_purchase: (metrics.real_time_result as number) ?? 0,
      // Weighted average video play times
      average_video_play: w.totalPlays > 0 ? Math.round((w.totalPlayTime / w.totalPlays) * 100) / 100 : 0,
      average_video_play_per_user: w.totalPlays > 0 ? Math.round((w.totalPlayTimePerUser / w.totalPlays) * 100) / 100 : 0,
    };
    // Remove the raw API key from the spread to avoid duplicate/confusing field
    delete dailyRecord.real_time_result;
    records.push(dailyRecord);
  }

  // ---- Build "adsSummary" record (single aggregate) ----
  // Aggregate from raw campaign rows for additive metrics.
  // Reach uses max across daily-aggregated values (from dailyMaxReach), NOT raw rows,
  // to match the same aggregation path as adsDailyOverview and fixture generator.
  const summary: Record<string, unknown> = {};
  let summaryTotalPlayTime = 0;
  let summaryTotalPlays = 0;
  let summaryTotalPlayTimePerUser = 0;

  for (const row of campaignRows) {
    for (const [key, rawValue] of Object.entries(row.metrics)) {
      const value = parseAdsMetricValue(rawValue);

      if (NON_ADDITIVE_METRICS.has(key) || key === "reach") continue;

      const prev = (summary[key] as number) ?? 0;
      summary[key] = prev + value;
    }

    const plays = parseAdsMetricValue(row.metrics.video_play_actions);
    const avgPlay = parseAdsMetricValue(row.metrics.average_video_play);
    const avgPlayPerUser = parseAdsMetricValue(row.metrics.average_video_play_per_user);
    summaryTotalPlayTime += avgPlay * plays;
    summaryTotalPlays += plays;
    summaryTotalPlayTimePerUser += avgPlayPerUser * plays;
  }

  // Summary reach = max across daily-aggregated reach values (same path as fixture generator)
  let summaryMaxReach = 0;
  for (const reach of dailyMaxReach.values()) {
    summaryMaxReach = Math.max(summaryMaxReach, reach);
  }

  const totalImpressions = (summary.impressions as number) ?? 0;
  const totalClicks = (summary.clicks as number) ?? 0;
  const totalSpend = (summary.spend as number) ?? 0;
  const totalConversions = (summary.conversions as number) ?? 0;
  const totalEngagements = (summary.engagements as number) ?? 0;
  const totalProfileVisits = (summary.profile_visits as number) ?? 0;
  const totalPurchaseVal = (summary.total_purchase_value as number) ?? 0;

  const startStr = dateRange.start.toISOString().split("T")[0]!;
  const adsSummaryRecord: Record<string, unknown> = {
    _reportType: "adsSummary",
    _date: startStr,
    ...summary,
    reach: summaryMaxReach,
    ctr: totalImpressions > 0 ? Math.round((totalClicks / totalImpressions) * 10000) / 10000 : 0,
    cpc: totalClicks > 0 ? Math.round((totalSpend / totalClicks) * 100) / 100 : 0,
    cpm: totalImpressions > 0 ? Math.round((totalSpend / totalImpressions * 1000) * 100) / 100 : 0,
    frequency: summaryMaxReach > 0 ? Math.round((totalImpressions / summaryMaxReach) * 100) / 100 : 0,
    cost_per_1000_reached: summaryMaxReach > 0 ? Math.round((totalSpend / summaryMaxReach * 1000) * 100) / 100 : 0,
    conversion_rate: totalClicks > 0 ? Math.round((totalConversions / totalClicks) * 10000) / 10000 : 0,
    cost_per_conversion: totalConversions > 0 ? Math.round((totalSpend / totalConversions) * 100) / 100 : 0,
    engagement_rate: totalImpressions > 0 ? Math.round((totalEngagements / totalImpressions) * 10000) / 10000 : 0,
    profile_visits_rate: totalImpressions > 0 ? Math.round((totalProfileVisits / totalImpressions) * 10000) / 10000 : 0,
    purchase_roas: totalSpend > 0 ? Math.round((totalPurchaseVal / totalSpend) * 100) / 100 : 0,
    total_onsite_shopping_roas: 0,
    // IMC-26: Remap real_time_result → real_time_purchase (API field → internal name)
    real_time_purchase: (summary.real_time_result as number) ?? 0,
    average_video_play: summaryTotalPlays > 0 ? Math.round((summaryTotalPlayTime / summaryTotalPlays) * 100) / 100 : 0,
    average_video_play_per_user: summaryTotalPlays > 0 ? Math.round((summaryTotalPlayTimePerUser / summaryTotalPlays) * 100) / 100 : 0,
  };
  // Remove the raw API key from the adsSummary record
  delete adsSummaryRecord.real_time_result;
  records.push(adsSummaryRecord);

  // ---- Build "shopPerformance" record if shop metrics exist ----
  const shopMetrics = ["product_page_views", "add_to_cart_shop", "initiate_checkout_shop", "complete_payment_shop", "gross_revenue", "gmv"];
  const hasShopData = shopMetrics.some((key) => ((summary[key] as number) ?? 0) > 0);
  if (hasShopData) {
    const shopRecord: Record<string, unknown> = { _reportType: "shopPerformance", _date: startStr };
    for (const key of shopMetrics) {
      shopRecord[key] = (summary[key] as number) ?? 0;
    }
    records.push(shopRecord);
  }

  return records;
}

// ============================================================================
// TikTok Business Adapter (Subtask 1.1)
// ============================================================================

export const tiktokBusinessAdapter: IntegrationAdapter = {
  platform: "tiktok-business",

  rateLimits: {
    requestsPerMinute: 600,
  },

  authType: "oauth",

  // --------------------------------------------------------------------------
  // fetchMetrics (Subtask 1.4)
  // --------------------------------------------------------------------------
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    // ---- Step 1: Fetch user stats (follower_count snapshot) ----
    const userInfoUrl = `${TIKTOK_API_BASE}/user/info/?fields=${USER_INFO_FIELDS}`;

    const userResult = await safeFetch(userInfoUrl, { headers: { Authorization: `Bearer ${credentials.accessToken}` } }, 10_000);

    let followerCount = 0;
    let profileViews = 0;

    if (userResult.success) {
      try {
        const userData = (await userResult.data.json()) as TikTokUserInfoResponse;

        // Check error.code
        const apiError = handleTikTokApiError(userData, "TikTok user info");
        if (apiError) return apiError;

        followerCount = userData.data?.user?.follower_count ?? 0;
        profileViews = userData.data?.user?.profile_view_count ?? 0;
      } catch (err) {
        return adapterErr({
          code: "invalid_response",
          message: sanitizeErrorMessage(
            `Failed to parse TikTok user info response: ${err instanceof Error ? err.message : String(err)}`
          ),
          retryable: false,
        });
      }
    } else {
      return handleTikTokFetchError(userResult);
    }

    // ---- Step 2: Fetch video list (paginated POST) ----
    const videosResult = await fetchTikTokVideos(credentials.accessToken);

    if (!videosResult.success) {
      return videosResult;
    }

    const allVideos = videosResult.data;

    // ---- Step 3 & 4: Filter and aggregate daily metrics ----
    const dayMetrics = aggregateVideoMetricsByDay(allVideos, dateRange);

    // Build dailySummary records for each day in range
    const records: Record<string, unknown>[] = [];
    const startDate = new Date(Date.UTC(dateRange.start.getUTCFullYear(), dateRange.start.getUTCMonth(), dateRange.start.getUTCDate()));
    const endDate = new Date(Date.UTC(dateRange.end.getUTCFullYear(), dateRange.end.getUTCMonth(), dateRange.end.getUTCDate()));

    let currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split("T")[0]!;
      const dayData = dayMetrics.get(dateStr);

      const viewCount = dayData?.view_count ?? 0;
      const likeCount = dayData?.like_count ?? 0;
      const commentCount = dayData?.comment_count ?? 0;
      const shareCount = dayData?.share_count ?? 0;
      const saveCount = dayData?.save_count ?? 0;
      const reachCount = dayData?.reach ?? 0;
      const impressionsCount = dayData?.impressions ?? 0;
      const totalTimeWatched = dayData?.total_time_watched ?? 0;
      const videosPublished = dayData?.videos_published ?? 0;

      // Derived metrics
      const totalEngagement = likeCount + commentCount + shareCount + saveCount;
      const engagementRate = viewCount > 0 ? totalEngagement / viewCount : 0;
      const avgViewsPerVideo = videosPublished > 0 ? viewCount / videosPublished : 0;

      // Weighted average watch time and completion rate from per-video data
      const avgTimeWatched = viewCount > 0
        ? (dayData?._total_avg_watch_time_weighted ?? 0) / viewCount
        : 0;
      const fullVideoWatchedRate = viewCount > 0
        ? (dayData?._total_full_watched_rate_weighted ?? 0) / viewCount
        : 0;

      // follower_change is 0 for live data (snapshot only)
      const followerChange = 0;

      records.push({
        _reportType: "dailySummary",
        _date: dateStr,
        date: dateStr,
        day_of_week: getDayOfWeek(dateStr),
        follower_count: followerCount,
        follower_change: followerChange,
        view_count: viewCount,
        like_count: likeCount,
        comment_count: commentCount,
        share_count: shareCount,
        save_count: saveCount,
        total_engagement: totalEngagement,
        engagement_rate: Math.round(engagementRate * 10000) / 10000,
        videos_published: videosPublished,
        avg_views_per_video: Math.round(avgViewsPerVideo * 100) / 100,
        reach: reachCount,
        impressions: impressionsCount,
        profile_views: profileViews, // snapshot — same value for all days (no historical API)
        average_time_watched: Math.round(avgTimeWatched * 100) / 100,
        total_time_watched: totalTimeWatched,
        full_video_watched_rate: Math.round(fullVideoWatchedRate * 10000) / 10000,
        // IMC-26: Account-level reach & impressions (derived from per-video aggregation)
        account_reach: reachCount,
        account_impressions: impressionsCount,
      });

      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    // Sort by date
    records.sort((a, b) => String(a.date).localeCompare(String(b.date)));

    // ---- Step 5: Build Top Videos records ----
    const topVideosRecords = buildTopVideosRecords(allVideos, dateRange);
    records.push(...topVideosRecords);

    // ---- Step 5b: Build traffic source breakdown ----
    const totalViews = records
      .filter((r) => r._reportType === "dailySummary")
      .reduce((sum, r) => sum + ((r.view_count as number) ?? 0), 0);

    if (totalViews > 0) {
      const startStr = dateRange.start.toISOString().split("T")[0]!;
      for (const ts of TRAFFIC_SOURCE_SHARES) {
        records.push({
          _reportType: "trafficSources",
          _date: startStr,
          traffic_source_type: ts.source,
          view_count: Math.round(totalViews * ts.share),
          share: Math.round(ts.share * 10000) / 10000,
        });
      }
    }

    // ---- Step 5c: Build demographics (AC #2 — requires 100+ followers) ----
    const demographicRecords = buildDemographicsRecords(followerCount, dateRange);
    records.push(...demographicRecords);

    const videoViewDemoRecords = buildVideoViewsDemographics(totalViews, followerCount, dateRange);
    records.push(...videoViewDemoRecords);

    // ---- Step 6: Fetch TikTok Ads data (if advertiser_id is available) ----
    // IMPORTANT: advertiser_id is a numeric Business Center ID from TikTok's Marketing API,
    // NOT the user's open_id (which is the organic Login Kit identifier stored in ctx.accountId).
    // The route handler passes it via AdapterContext when the integration row contains
    // an advertiserId in its metadata column. If absent, ads fetch is skipped (organic-only).
    // NOTE (Story 31.22): discoverAccounts now returns the FULL advertiser catalog
    // (metadata.accountType === "advertiser" entries, ids on this exact advertiser_id axis),
    // so the grant-resource catalog is the data source for this value. Wiring the picked
    // advertiserId into ctx server-side (retiring this untyped cast) is Story 31-29's job.
    const advertiserId = (ctx as { advertiserId?: string }).advertiserId ?? null;
    if (advertiserId) {
      const adsRecords = await fetchTikTokAdsData(
        credentials.accessToken,
        advertiserId,
        dateRange
      );
      records.push(...adsRecords);
    }

    return adapterOk({
      platform: "tiktok-business",
      accountId: ctx.accountId ?? "",
      dateRange: { start: dateRange.start, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  // --------------------------------------------------------------------------
  // validateConnection (Subtask 1.5)
  // --------------------------------------------------------------------------
  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const url = `${TIKTOK_API_BASE}/user/info/?fields=open_id`;

    const result = await safeFetch(
      url,
      { headers: { Authorization: `Bearer ${credentials.accessToken}` } },
      5_000
    );

    if (!result.success) {
      return handleTikTokFetchError(result);
    }

    let responseBody: TikTokUserInfoResponse;
    try {
      responseBody = (await result.data.json()) as TikTokUserInfoResponse;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "Failed to parse TikTok validation response",
        retryable: false,
      });
    }

    // Check error.code === "ok"
    const apiError = handleTikTokApiError(responseBody, "TikTok validation");
    if (apiError) return apiError;

    if (!responseBody.data?.user?.open_id) {
      return adapterErr({
        code: "invalid_response",
        message: "TikTok user info response missing open_id",
        retryable: false,
      });
    }

    return adapterOk(true);
  },

  // --------------------------------------------------------------------------
  // discoverAccounts (Subtask 1.6; hybrid catalog: Story 31.22)
  // --------------------------------------------------------------------------
  /**
   * Hybrid-catalog discovery (Story 31.22).
   *
   * The registry classifies tiktok-business as `oauthGrantScope:
   * "multi-tenant"` (Story 31-5): ONE platform row fronts TWO provider
   * APIs behind ONE token, so the catalog mixes resource kinds —
   * exactly 1 organic user account (Login Kit v2 is 1:1 per grant) PLUS
   * 0..N Marketing-API advertisers (the multi-tenant axis). Every entry
   * carries `metadata.accountType: "organic_user" | "advertiser"` so
   * downstream consumers (31-24 cron, 31-29 picker) never have to parse
   * id formats; the organic `open_id` is FROZEN (it is `ctx.accountId`,
   * the PUT /account selection value, and the catalog
   * `external_account_id` for existing integrations), and advertiser ids
   * are the bare numeric `advertiser_id` axis `fetchTikTokAdsReport`
   * consumes (see fetchMetrics Step 6).
   *
   * Advertiser availability is a CAPABILITY, not a precondition:
   * - `ad.read` missing from the persisted grant scopes → silent
   *   organic-only skip (expected steady-state for unapproved apps);
   * - `TIKTOK_CLIENT_KEY`/`TIKTOK_CLIENT_SECRET` absent from `ctx.env`
   *   (the legacy /accounts route passes `{KV, DB}` only) → warned
   *   organic-only skip;
   * - permission-class advertiser-list failure (app not approved /
   *   Login-Kit-only token) → warned organic-only skip.
   * When the gate PASSES and the ads surface fails for any other reason,
   * discovery fails LOUDLY — a silently-partial catalog would make the
   * 31-24 cron mark live advertisers as revoked.
   */
  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    const url = `${TIKTOK_API_BASE}/user/info/?fields=open_id,display_name,avatar_url`;

    const result = await safeFetch(
      url,
      { headers: { Authorization: `Bearer ${credentials.accessToken}` } },
      10_000
    );

    if (!result.success) {
      return handleTikTokFetchError(result);
    }

    let responseBody: TikTokUserInfoResponse;
    try {
      responseBody = (await result.data.json()) as TikTokUserInfoResponse;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "Failed to parse TikTok user info response",
        retryable: false,
      });
    }

    const apiError = handleTikTokApiError(responseBody, "TikTok account discovery");
    if (apiError) return apiError;

    const user = responseBody.data?.user;
    if (!user?.open_id) {
      return adapterErr({
        code: "invalid_response",
        message: "TikTok user info missing open_id",
        retryable: false,
      });
    }

    // Organic entry always comes FIRST (it is the primary the OAuth callback
    // auto-attaches). id format frozen — see method JSDoc.
    const organicAccount: AccountInfo = {
      id: user.open_id,
      name: user.display_name || user.open_id,
      platform: "tiktok-business",
      scopes: [...TIKTOK_ORGANIC_SCOPES],
      metadata: { avatarUrl: user.avatar_url ?? null, accountType: "organic_user" },
    };

    // ---- Capability gate (AC #6) ----
    // (a) ad.read absent from the PERSISTED grant scopes — expected
    // steady-state for apps without Marketing API approval; silent skip.
    // The gate reads credentials.scopes only to SKIP work, never to grant
    // access (31-4: scopes are display/consent metadata, not access control).
    const grantedScopes = credentials.scopes ?? [];
    if (!grantedScopes.includes("ad.read")) {
      return adapterOk([organicAccount]);
    }

    // (b) App credentials absent from ctx.env (same cast pattern as
    // refreshToken). The legacy /accounts server route passes {KV, DB} only,
    // so this keeps its response additive-compatible with today.
    const env = ctx.env as {
      TIKTOK_CLIENT_KEY?: string;
      TIKTOK_CLIENT_SECRET?: string;
    };
    if (!env.TIKTOK_CLIENT_KEY || !env.TIKTOK_CLIENT_SECRET) {
      console.warn(
        JSON.stringify({
          event: "tiktok_advertiser_discovery_skipped",
          reason: "missing_app_credentials",
          tenantId: ctx.tenantId,
          integrationId: ctx.integrationId,
          message:
            "TikTok advertiser discovery skipped: TIKTOK_CLIENT_KEY/TIKTOK_CLIENT_SECRET not present in adapter env; returning organic-only catalog",
        })
      );
      return adapterOk([organicAccount]);
    }

    // ---- Advertiser list (AC #1) ----
    const listResult = await fetchTikTokAdvertiserList(
      credentials.accessToken,
      env.TIKTOK_CLIENT_KEY,
      env.TIKTOK_CLIENT_SECRET
    );

    if (!listResult.success) {
      // (c) Permission-class failure = ads surface unauthorized for this
      // token (app not approved / Login-Kit-only token — pre-mortem A):
      // degrade to organic-only with a structured warn. The organic call
      // above already proved the token itself is valid.
      if (listResult.error.code === "permission_denied") {
        console.warn(
          JSON.stringify({
            event: "tiktok_advertiser_discovery_skipped",
            reason: "ads_surface_unauthorized",
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            errorMessage: listResult.error.message,
          })
        );
        return adapterOk([organicAccount]);
      }
      // AC #7 — gate passed but the ads surface is BROKEN (rate limit, 5xx,
      // network, malformed response): fail loudly with the mapped per-cause
      // code/retryable flag. NEVER a silently-partial organic-only success.
      return listResult;
    }

    // AC #9 — zero advertisers is a legitimate state, not a degrade: no warn.
    const advertisers = listResult.data;
    if (advertisers.length === 0) {
      return adapterOk([organicAccount]);
    }

    // ---- Enrichment (AC #2) — best-effort, never affects membership ----
    // Dedupe ids BEFORE enrichment (insertion order preserved): an echoed
    // advertiser id would otherwise be sent twice across the advertiser_ids
    // batches, wasting quota and skewing chunk boundaries.
    const infoResult = await fetchTikTokAdvertiserInfo(
      credentials.accessToken,
      [...new Set(advertisers.map((a) => a.advertiserId))],
      { tenantId: ctx.tenantId, integrationId: ctx.integrationId }
    );
    let infoMap = new Map<string, { name?: string; currency?: string; timezone?: string; displayTimezone?: string; status?: string }>();
    if (infoResult.success) {
      infoMap = infoResult.data;
    } else {
      // Defensive: the helper contract is "never errs", but if that ever
      // changes, enrichment failure must still degrade metadata only.
      console.warn(
        JSON.stringify({
          event: "tiktok_advertiser_enrichment_failed",
          tenantId: ctx.tenantId,
          integrationId: ctx.integrationId,
          chunkSize: advertisers.length,
          errorCode: infoResult.error.code,
          message:
            "TikTok advertiser info enrichment failed; advertisers remain in the catalog without enrichment metadata",
        })
      );
    }

    // ---- Mapping + deterministic ordering (AC #3) ----
    const advertiserAccounts: AccountInfo[] = advertisers
      .slice()
      .sort((a, b) => compareAdvertiserIds(a.advertiserId, b.advertiserId))
      .map((adv) => {
        const info = infoMap.get(adv.advertiserId);
        // Omit absent enrichment keys entirely — metadata flows into
        // external_account_metadata JSON (no `undefined` values).
        const metadata: Record<string, unknown> = { accountType: "advertiser" };
        if (info?.currency) metadata.currency = info.currency;
        if (info?.timezone) metadata.timezone = info.timezone;
        if (info?.displayTimezone) metadata.displayTimezone = info.displayTimezone;
        if (info?.status) metadata.status = info.status;
        return {
          id: adv.advertiserId,
          name: adv.advertiserName || info?.name || `Advertiser ${adv.advertiserId}`,
          platform: "tiktok-business" as const,
          scopes: [...TIKTOK_ADS_SCOPES],
          metadata,
        };
      });

    // ---- Defensive dedupe by id, first occurrence wins (AC #5) ----
    // DB UNIQUE index is the backstop, not the first line (ADR-0004 §AP-4).
    const deduped = new Map<string, AccountInfo>();
    for (const account of [organicAccount, ...advertiserAccounts]) {
      if (!deduped.has(account.id)) {
        deduped.set(account.id, account);
      }
    }

    return adapterOk([...deduped.values()]);
  },

  // --------------------------------------------------------------------------
  // refreshToken (Subtask 1.9)
  // --------------------------------------------------------------------------
  async refreshToken(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_expired",
        message: "No refresh token available. User must re-authorize TikTok.",
        retryable: false,
      });
    }

    const env = ctx.env as {
      TIKTOK_CLIENT_KEY?: string;
      TIKTOK_CLIENT_SECRET?: string;
    };

    if (!env.TIKTOK_CLIENT_KEY || !env.TIKTOK_CLIENT_SECRET) {
      return adapterErr({
        code: "invalid_response",
        message: "TikTok client credentials not configured",
        retryable: false,
      });
    }

    try {
      const result = await refreshTikTokAccessToken(
        credentials.refreshToken,
        env.TIKTOK_CLIENT_KEY,
        env.TIKTOK_CLIENT_SECRET
      );

      const expiresAt = new Date(Date.now() + result.expires_in * 1000);

      // CRITICAL: TikTok MAY rotate refresh tokens — always return whatever response provides
      return adapterOk({
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        expiresAt,
      });
    } catch (err) {
      return adapterErr({
        code: "token_expired",
        message: sanitizeErrorMessage(
          `TikTok token refresh failed: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }
  },

  // --------------------------------------------------------------------------
  // getAvailableMetrics (Subtask 1.7)
  // --------------------------------------------------------------------------
  getAvailableMetrics(): MetricDefinition[] {
    return TIKTOK_BUSINESS_METRICS;
  },

  // --------------------------------------------------------------------------
  // getAvailableDimensions (Subtask 1.8)
  // --------------------------------------------------------------------------
  getAvailableDimensions(): DimensionDefinition[] {
    return TIKTOK_BUSINESS_DIMENSIONS;
  },
};
